import { useMutation, useQueryClient } from '@tanstack/react-query'
import { set as _set } from 'radash'

import { getLocalLikesActive } from '@wwt/core/admin'
import { Offer } from '@wwt/entities/search-response'

import { getFullRoomId } from '@/entities/offer/index.ts'
import { setKeys, useActiveSet } from '@/entities/sets'
import { SetKeys, SetTypes } from '@/entities/sets/config/setKeys.ts'
import { SetMutationUtils } from '@/entities/sets/utils/setMutationUtils.ts'
import { assert } from '@/shared/lib/assert.ts'

import {
	DislikeKeys,
	DislikeTypes,
	dislikeKeys
} from '../config/dislikeQueryKeys.ts'
import { LikeKeys, LikeTypes, likeKeys } from '../config/likeQueryKeys.ts'
import { dislikeQuery } from '../query/dislikeQuery.ts'
import { DislikeMutationUtils } from '../utils/dislikeMutationUtils.ts'
import { LikeMutationUtils } from '../utils/likeMutationUtils.ts'

export const useDislikeMutation = () => {
	const queryClient = useQueryClient()
	const activeSet = useActiveSet()

	return useMutation({
		mutationKey: dislikeKeys.create(),
		mutationFn: async (offer: Offer) => {
			if (getLocalLikesActive()) {
				return
			}

			return await dislikeQuery(offer.IDs)
		},
		onMutate: async (offer: Offer) => {
			const keys = {
				sets: setKeys.all(),
				likes: likeKeys.all(activeSet?.id),
				ids: likeKeys.ids(),
				dislikes: dislikeKeys.all()
			}

			/* prettier-ignore */
			const previous = {
				sets: queryClient.getQueryData<SetTypes['all'], SetKeys['all']>(keys.sets),
				likes: queryClient.getQueryData<LikeTypes['all'], LikeKeys['all']>(keys.likes),
				ids: queryClient.getQueryData<LikeTypes['ids'], LikeKeys['ids']>(keys.ids),
				dislikes: queryClient.getQueryData<DislikeTypes['all'], DislikeKeys['all']>(keys.dislikes)
			}

			const isDisliked = Boolean(
				previous.dislikes &&
					previous.dislikes.disliked.includes(getFullRoomId(offer))
			)

			assert(
				isDisliked === false,
				'Offer should be NOT disliked for call dislike mutation'
			)

			await queryClient.cancelQueries({ queryKey: keys.likes })
			await queryClient.cancelQueries({ queryKey: keys.ids })
			await queryClient.cancelQueries({ queryKey: keys.dislikes })

			queryClient.setQueryData<DislikeTypes['all'], DislikeKeys['all']>(
				keys.dislikes,
				saved => DislikeMutationUtils.addOfferToDisliked(saved, offer)
			)

			const offerId = offer.IDs.nonPersistingOfferId
			const isLiked = Boolean(previous.ids && previous.ids[offerId])

			if (!isLiked) {
				return {
					keys,
					previous
				}
			}

			queryClient.setQueryData<SetTypes['all'], SetKeys['all']>(
				keys.sets,
				saved =>
					SetMutationUtils.addNumberToTotalOffers(saved, activeSet?.id, -1)
			)

			queryClient.setQueryData<LikeTypes['ids'], LikeKeys['ids']>(
				keys.ids,
				saved => LikeMutationUtils.removeOfferFromLikeIds(saved, offer)
			)

			/**
			 * Remove offer from saved likes list if server likes is not active and if offer liked
			 *
			 * On active server likes we invalidate it
			 */
			if (getLocalLikesActive()) {
				queryClient.setQueryData<LikeTypes['all'], LikeKeys['all']>(
					keys.likes,
					saved => LikeMutationUtils.removeOfferFromLikesList(saved, offer)
				)
			}

			return {
				keys,
				previous
			}
		},

		/* prettier-ignore */
		onError: (_err, _disliked, context) => {
			assert(context !== undefined, 'onError context not found in dislike mutation')

			queryClient.setQueryData<SetTypes['all'], SetKeys['all']>(context.keys.sets, context.previous.sets)
			queryClient.setQueryData<LikeTypes['all'], LikeKeys['all']>(context.keys.likes, context.previous.likes)
			queryClient.setQueryData<LikeTypes['ids'], LikeKeys['ids']>(context.keys.ids, context.previous.ids)
			queryClient.setQueryData<DislikeTypes['all'], DislikeKeys['all']>(context.keys.dislikes, context.previous.dislikes)
		},

		/* prettier-ignore */
		onSettled: async (_dislike, _err, _offer, context) => {
			assert(context !== undefined, 'onSettled context not found in dislike mutation')

			await queryClient.cancelQueries({ queryKey: context.keys.likes })
			await queryClient.cancelQueries({ queryKey: context.keys.ids })
			await queryClient.cancelQueries({ queryKey: context.keys.dislikes })

			await queryClient.invalidateQueries({ queryKey: context.keys.dislikes })
			await queryClient.invalidateQueries({ queryKey: context.keys.likes })
			await queryClient.invalidateQueries({ queryKey: context.keys.ids })
		}
	})
}
